import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SocialIcon } from 'react-social-icons';
import { Sun, Moon, Gift } from 'lucide-react';
import { useTelegram } from '../hooks/useTelegram';
import PlayerStats from './PlayerStats';
import Leaderboard from './Leaderboard';
import ActionButtons from './ActionButtons';
import FAQ from './FAQ';
import ScrollingInfo from './ScrollingInfo';
import DevModeIndicator from './DevModeIndicator';
import HighScore from './HighScore';
import MusicPlayer from './MusicPlayer';
import ViewAttempts from './ViewAttempts';
import TokenDialog from './TokenDialog';
import { useButtonSound } from '../utils/audio';
import HowToPlayModal from './HowToPlayModal';
import '../styles/attempts.css';
import '../styles/labels.css';
import FloatingEmojis from './FloatingEmojis';
import GameRules from './GameRules';
import RoundInfo from './RoundInfo';

// Import images
import telegramIcon from '../assets/icons/telegram.png';
import xIcon from '../assets/icons/x.png';
import dexscreenerIcon from '../assets/icons/dexscreener.png';
import dextoolsIcon from '../assets/icons/dextools.png';
import birdeyeIcon from '../assets/icons/birdeye.png';
import jupiterIcon from '../assets/icons/jupiter.png';

const LightModeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" width="20" height="20">
    <path d="M13 2h-2v4h2V2Zm2 6H9v2H7v4h2v4h6v-4h2v-4h-2V8Zm0 2v4h-2v2h-2v-2H9v-4h6ZM9 20h6v2H9v-2Zm14-9v2h-4v-2h4ZM5 13v-2H1v2h4Zm12-7h2v2h-2V6Zm2 0h2V4h-2v2ZM5 6h2v2H5V6Zm0 0V4H3v2h2Z" />
  </svg>
);

const DarkModeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" width="20" height="20">
    <path d="M8 2h8v2H8V2ZM6 6V4h2v2H6Zm0 6H4V6h2v6Zm2 2H6v-2h2v2Zm2 0H8v4h8v-4h2v-2h2V6h-2V4h-2v2h2v6h-2v2h-2v2h-4v-2Zm2-2v2h-2v-2h2Zm0-2h2v2h-2v-2Zm0-2v2h-2V8h2Zm0 0V6h2v2h-2Zm4 14v-2H8v2h8Z" />
  </svg>
);

const ShareIcon = () => (
  <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20">
    <path d="M19 12v8h-7m7-8h2V8h-3m1 4H5m13-4V4h-6m6 4H6m0 0V4h6M6 8H3v4h2m0 0v8h7m0 0V4" stroke="currentColor" stroke-width="2"/>
  </svg>
);

export default function App() {
  const [isDark, setIsDark] = useState(false);
  const [showAttempts, setShowAttempts] = useState(false);
  const [showTokenDialog, setShowTokenDialog] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [showGameRules, setShowGameRules] = useState(false);
  const [devMode] = useState(process.env.NODE_ENV === 'development');
  const { telegramId, isLoading } = useTelegram();
  const { playSound } = useButtonSound();
  const [showTooltip, setShowTooltip] = useState(false);
  const tokenMint = import.meta.env.VITE_SPL_TOKEN_MINT;
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    document.body.classList.toggle('dark-theme', isDark);
  }, [isDark]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowTooltip(true);
    }, 4500);
    return () => clearTimeout(timer);
  }, []);

  const handleSocialClick = (url: string) => {
    window.open(url, '_blank');
  };

  const handleTweetClick = () => {
    playSound();
    const tweetText = encodeURIComponent("I'm playing @breakme_ai! Join me at https://t.me/breakme_ai_bot");
    window.open(`https://twitter.com/intent/tweet?text=${tweetText}`, '_blank');
  };

  if (isLoading) {
    return (
      <div className="nes-container is-rounded">
        <p>Loading game...</p>
      </div>
    );
  }

  if (!telegramId && !devMode && !isLoading) {
    return (
      <div className="nes-container is-rounded">
        <p>Please open this app in Telegram</p>
      </div>
    );
  }

  return (
    <div className={`app ${isDark ? 'dark-theme' : ''}`}>
      <ScrollingInfo />
      
      <div className="app-container">
        <FloatingEmojis />
        <div className="controls-section">
          <div className="social-links">
            <button 
              className="nes-btn is-small social-button telegram"
              onClick={() => handleSocialClick('https://t.me/breakmeai')}
            >
              <img src={telegramIcon} alt="Telegram" />
            </button>
            <button 
              className="nes-btn is-small social-button x"
              onClick={() => handleSocialClick('https://x.com/breakme_ai')}
            >
              <img src={xIcon} alt="X" />
            </button>
            <button 
              className="nes-btn is-small social-button dexscreener"
              onClick={() => handleSocialClick(`https://dexscreener.com/solana/${tokenMint}`)}
            >
              <img src={dexscreenerIcon} alt="Dexscreener" />
            </button>
            <button 
              className="nes-btn is-small social-button dextools"
              onClick={() => handleSocialClick(`https://www.dextools.io/app/solana/pair-explorer/${tokenMint}`)}
            >
              <img src={dextoolsIcon} alt="Dextools" />
            </button>
            <button 
              className="nes-btn is-small social-button birdeye"
              onClick={() => handleSocialClick(`https://birdeye.so/token/${tokenMint}?chain=solana`)}
            >
              <img src={birdeyeIcon} alt="Birdeye" />
            </button>
            <button 
              className="nes-btn is-small social-button jupiter"
              onClick={() => handleSocialClick(`https://jup.ag/swap/SOL-${tokenMint}`)}
            >
              <img src={jupiterIcon} alt="Jupiter" />
            </button>
          </div>
          <div className="controls-buttons">
            <div className="tweet-button-container" style={{ position: 'relative' }}>
              <div 
                className="tweet-tooltip" 
                style={{ 
                  position: 'absolute',
                  top: '-40px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 1000,
                  opacity: 0,
                  visibility: 'hidden',
                  background: 'var(--light-bg)',
                  border: '2px solid var(--dark-text)',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  cursor: 'pointer',
                  transition: 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out'
                }}
                ref={(el) => {
                  if (el) {
                    setTimeout(() => {
                      el.style.opacity = '1';
                      el.style.visibility = 'visible';
                      
                      setTimeout(() => {
                        el.style.opacity = '0';
                        el.style.visibility = 'hidden';
                      }, 10000);
                    }, 4000);
                  }
                }}
                onClick={(e) => {
                  e.currentTarget.style.opacity = '0';
                  e.currentTarget.style.visibility = 'hidden';
                }}
              >
                Rewards coming soon
              </div>
              <button 
                className="nes-btn is-small tweet-button is-disabled"
                aria-label="Rewards coming soon"
                disabled
              >
                <Gift />
              </button>
              {showTooltip && (
                <div className="nes-balloon tweet-tooltip from-right">
                  <p>Rewards feature coming soon!</p>
                </div>
              )}
            </div>
            <button 
              className="nes-btn is-small rules-button"
              onClick={() => {
                playSound();
                setShowGameRules(true);
              }}
            >
              Rules
            </button>
            <MusicPlayer />
            <button 
              className={`nes-btn is-small ${isDark ? 'is-primary' : 'is-warning'}`} 
              onClick={() => {
                playSound();
                setIsDark(!isDark);
              }}
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDark ? <LightModeIcon /> : <DarkModeIcon />}
            </button>
          </div>
        </div>

        <div className="game-section">
          <div className="nes-container with-title">
            <p className="title">
              Game #02
              <span className="experimental-label">EXPERIMENTAL (still)</span>
              <button 
                className="nes-btn is-small details-toggle"
                onClick={() => setShowDetails(!showDetails)}
                style={{ 
                  float: 'right',
                  marginLeft: '1rem'
                }}
              >
                {showDetails ? 'Basic' : 'Round Details'}
              </button>
            </p>
            <div style={{ marginTop: '1rem' }}>
              {!showDetails ? (
                <HighScore />
              ) : (
                <div className="detailed-info">
                  <div className="metric-grid">
                    <div className="metric-item">
                      <label>Current Bounty:</label>
                      <span className="value">1.500 SOL</span>
                    </div>
                    <div className="metric-item">
                      <label>Total Attempts:</label>
                      <span className="value">150</span>
                    </div>
                    <div className="metric-item">
                      <label>Price (3x Attempts):</label>
                      <span className="value">0.050 SOL</span>
                    </div>
                    <div className="metric-item">
                      <label>Next Price Increase:</label>
                      <span className="value">In 50 attempts</span>
                    </div>
                    <div className="metric-item">
                      <label>Price Model:</label>
                      <span className="value">Factor Increase</span>
                    </div>
                    <div className="metric-item">
                      <label>Price:Bounty Ratio:</label>
                      <span className="value">1:30</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <ActionButtons 
            onPlayClick={() => {
              playSound();
              setShowAttempts(false);
              setShowHowToPlay(false);
            }} 
            onGetTokensClick={() => {
              playSound();
              setShowTokenDialog(true);
            }} 
            onHowToPlayClick={() => {
              playSound();
              setShowHowToPlay(true);
            }}
            onViewAttemptsClick={() => {
              playSound();
              setShowAttempts(true);
              document.body.style.overflow = 'hidden';
            }} 
          />

          {showAttempts ? (
            <div className="attempts-overlay">
              <ViewAttempts 
                isDark={isDark}
                onClose={() => {
                  playSound();
                  setShowAttempts(false);
                  document.body.style.overflow = 'auto';
                }} 
              />
            </div>
          ) : (
            <>
              <div className="nes-container with-title">
                <p className="title">Game Stats</p>
                <div className={`player-stats-wrapper ${!telegramId ? 'blurred' : ''}`}>
                  <PlayerStats telegramId={telegramId || '123456789'} />
                  {!telegramId && (
                    <div className="blur-overlay">
                      <p>Open in Telegram to view stats</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="full-width-section">
                <div className="nes-container with-title">
                  <p className="title">FAQ</p>
                  <FAQ />
                </div>
              </div>

              <div className="full-width-section">
                <div className="nes-container with-title">
                  <p className="title">Ecosystem</p>
                  <div className="nav-links">
                    <Link to="/about" className="nes-btn">About</Link>
                    <Link to="/tokenomics" className="nes-btn">Tokenomics</Link>
                    <Link to="/updates" className="nes-btn">Updates</Link>
                  </div>
                </div>
              </div>

              <div className="full-width-section">
                <Leaderboard />
              </div>
            </>
          )}
        </div>
      </div>

      <GameRules 
        isOpen={showGameRules} 
        onClose={() => setShowGameRules(false)} 
      />

      {showHowToPlay && (
        <HowToPlayModal 
          isDark={isDark}
          onClose={() => setShowHowToPlay(false)}
          onPlayClick={() => {
            setShowHowToPlay(false);
            setShowAttempts(false);
          }}
        />
      )}

      {showTokenDialog && (
        <TokenDialog 
          isDark={isDark} 
          onClose={() => setShowTokenDialog(false)} 
        />
      )}

      {devMode && <DevModeIndicator />}

      <footer className="app-footer">
        <div>
          © {new Date().getFullYear()} BreakMe.ai™. All rights reserved.
        </div>
        <div className="legal-links">
          <Link to="/privacy-policy">Privacy Policy</Link>
          <span className="separator">|</span>
          <Link to="/terms-of-use">Terms of Use</Link>
          <span className="separator">|</span>
          <Link to="/disclaimer">Disclaimer</Link>
        </div>
      </footer>
    </div>
  );
} 